/** Super Admin → Admin Team: invite Super Admins / Assistant Managers, switch areas on or off, suspend or remove. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, PageHeader, StatusPill, btnPrimary, btnSmall, errorText, inputCls } from '../../components/finance/financeUi';
import { Switch } from '../../components/ui/switch';

const LEVEL = { super_admin: 'Super Admin', assistant_manager: 'Assistant Manager' };

function AreaSwitches({ areas, value, onChange, disabled, idp }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">{areas.map((a) => (
      <label key={a.key} className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2 text-sm">
        <span>{a.label}</span><Switch checked={!!value[a.key]} disabled={disabled} onCheckedChange={(v) => onChange({ ...value, [a.key]: v })} data-testid={`${idp}-area-${a.key}`} />
      </label>))}
    </div>
  );
}

function InviteCard({ areas, onDone }) {
  const blank = { first_name: '', last_name: '', email: '', level: 'assistant_manager', areas: Object.fromEntries(areas.map((a) => [a.key, a.default])) };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.request('/admin/team', { method: 'POST', body: JSON.stringify(f) }); toast.success(`Invite sent to ${f.email}`); setF(blank); onDone(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Add an admin" testid="admin-invite-card">
      <div className="space-y-4 px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <input className={inputCls} placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="admin-first-name" />
          <input className={inputCls} placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="admin-last-name" />
          <input className={inputCls} placeholder="email@merchantsolutionscorp.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email" />
          <select className={inputCls} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} data-testid="admin-level">{Object.entries(LEVEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        {f.level === 'assistant_manager' && <AreaSwitches areas={areas} value={f.areas} onChange={(v) => setF({ ...f, areas: v })} idp="invite" />}
        <button type="button" className={btnPrimary} disabled={busy} onClick={send} data-testid="admin-invite-btn">{busy ? 'Sending…' : 'Send invite'}</button>
        <p className="text-xs text-muted-foreground">They get an email to set their own password (link works once, 1 hour). Every admin signs in with an emailed security code.</p>
      </div>
    </Card>
  );
}

function AdminRow({ a, areas, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ level: a.level, areas: a.areas });
  const save = async (body, msg) => { try { await api.request(`/admin/team/${a.user_id}`, { method: 'PUT', body: JSON.stringify(body) }); toast.success(msg); onDone(); } catch (e) { toast.error(errorText(e)); } };
  return (
    <div className="space-y-3 py-3" data-testid={`admin-row-${a.user_id}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="min-w-0 flex-1"><b>{`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email}</b><span className="block text-xs text-muted-foreground">{a.email}</span></span>
        <span className="text-xs">{LEVEL[a.level]}</span><StatusPill value={a.status === 'active' ? 'ACTIVE' : a.status.toUpperCase()} />
        {!a.password_set && a.status === 'active' && <button type="button" className={btnSmall} onClick={() => api.request(`/admin/team/${a.user_id}/resend`, { method: 'POST' }).then(() => toast.success('Invite re-sent')).catch((e) => toast.error(errorText(e)))} data-testid={`admin-resend-${a.user_id}`}>Resend invite</button>}
        <button type="button" className={btnSmall} onClick={() => setOpen(!open)} data-testid={`admin-edit-${a.user_id}`}>{open ? 'Close' : 'Change access'}</button>
        <button type="button" className={btnSmall} onClick={() => save({ status: a.status === 'active' ? 'suspended' : 'active' }, a.status === 'active' ? 'Suspended' : 'Reactivated')} data-testid={`admin-suspend-${a.user_id}`}>{a.status === 'active' ? 'Suspend' : 'Reactivate'}</button>
        <button type="button" className={`${btnSmall} text-rose-600`} onClick={() => window.confirm(`Remove ${a.email} as an admin?`) && save({ status: 'removed' }, 'Removed')} data-testid={`admin-remove-${a.user_id}`}>Remove</button>
      </div>
      {open && <div className="space-y-3 rounded-lg bg-muted/40 p-3">
        <select className={`${inputCls} max-w-xs`} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} data-testid={`admin-edit-level-${a.user_id}`}>{Object.entries(LEVEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        {f.level === 'assistant_manager' && <AreaSwitches areas={areas} value={f.areas} onChange={(v) => setF({ ...f, areas: v })} idp={`edit-${a.user_id}`} />}
        <button type="button" className={btnPrimary} onClick={() => save(f, 'Access updated')} data-testid={`admin-save-${a.user_id}`}>Save access</button>
      </div>}
    </div>
  );
}

export default function AdminTeamPage() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.request('/admin/team').then(setD).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6 pb-24" data-testid="admin-team-page">
      <PageHeader eyebrow="Platform (MSC)" title="Admin Team" subtitle="Super Admins have full control. Assistant Managers only see the areas you switch on — switched-off areas disappear from their menu, pages and data." />
      <InviteCard areas={d.areas} onDone={load} />
      <Card title={`${d.admins.length} admins`} testid="admin-list"><div className="divide-y px-5">{d.admins.map((a) => <AdminRow key={`${a.user_id}-${a.level}-${a.status}`} a={a} areas={d.areas} onDone={load} />)}</div></Card>
    </div>
  );
}
